import React, { FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  fetchAiSlotById,
  selectAiSlotById,
  selectIsAiSlotIdInProgress,
} from '@store/slices/aiSlot.slice'
import {
  AiLinkType,
  AIMetadataModel,
  ArticleResource,
  createAiMetadata,
  selectAIMetadataById,
  updateAiMetadata,
} from '@store/slices/aiMeta.slice'
import { toHourMinString } from '@shared/tools/time.tools'
import { AppSelectComponent } from '@components/shared/appSelect.component'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import AppButton from '@components/shared/appButton'
import { Card } from 'react-bootstrap'
import { Floppy2, GearFill, MusicNoteList } from 'react-bootstrap-icons'
import { selectIsPlaylistIdInProgress } from '@store/slices/playlist.slice'
import { handleGenerateSlotOutline } from '@services/aislot.service'

const SlotOutlineComponent: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { slotId, playlistId } = useParams()
  const aiSlot = useSelector((state: RootState) =>
    slotId ? selectAiSlotById(state, slotId) : null
  )
  const isSlotLoading = useSelector((state: RootState) =>
    aiSlot ? selectIsAiSlotIdInProgress(state, aiSlot.id) : null
  )
  const playlistLoading = useSelector((state: RootState) =>
    playlistId ? selectIsPlaylistIdInProgress(state, playlistId) : null
  )
  const aiMetadata: AIMetadataModel | null = useSelector((state: RootState) =>
    aiSlot?.aiEntryMetadata
      ? selectAIMetadataById(state, aiSlot.aiEntryMetadata)
      : null
  )
  const [selectedType, setSelectedType] = useState<AiLinkType | undefined>(
    aiMetadata?.aiLinkType
  )
  const [regionalized, setRegionalized] = useState<boolean>(
    aiMetadata ? aiMetadata?.regionalSignature : false
  )
  const aiTypeOption = Object.values(AiLinkType).map((aiType) => ({
    id: aiType,
    label: aiType,
  }))

  useEffect(() => {
    if (!aiSlot?.aiEntryMetadata) {
      setSelectedType(undefined)
      setRegionalized(false)
    }
  }, [aiSlot])

  useEffect(() => {
    aiMetadata && setSelectedType(aiMetadata?.aiLinkType)
  }, [aiMetadata?.aiLinkType])

  useEffect(() => {
    aiMetadata && setRegionalized(aiMetadata.regionalSignature)
  }, [aiMetadata?.regionalSignature])

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegionalized(event.target.checked)
  }
  const onTypeSelectChange = (typeId: EntityId | null) => {
    typeId && setSelectedType(typeId as AiLinkType)
  }

  const onSaveChanges = () => {
    const data: Partial<AIMetadataModel> = {}
    if (regionalized !== aiMetadata?.regionalSignature) {
      data.regionalSignature = regionalized
    }
    if (selectedType !== aiMetadata?.aiLinkType) {
      data.aiLinkType = selectedType
    }
    if (aiMetadata)
      return dispatch(updateAiMetadata({ id: aiMetadata.id, data }))
    else if (aiSlot) {
      return dispatch(createAiMetadata({ ...data, aiSlotId: aiSlot.id }))
        .then(unwrapResult)
        .then(() => {
          dispatch(fetchAiSlotById(aiSlot.id))
        })
    }
  }

  const onRegenerateSlot = () => {
    if (aiSlot && playlistId) {
      toast.info('Generuji outline')
      return handleGenerateSlotOutline(aiSlot, playlistId)
    }
  }

  const isSaveDisabled =
    regionalized === aiMetadata?.regionalSignature &&
    (!selectedType || selectedType === aiMetadata?.aiLinkType)

  const articleSourceDetail = (
    articleSource: ArticleResource,
    index: number
  ) => {
    return (
      <div className={'d-flex flex-column ms-2 mb-2'} key={index}>
        <span>
          <a target="_blank" href={articleSource.url} rel="noreferrer">
            {articleSource.id}
          </a>
        </span>
        <span>{articleSource.summary}</span>
      </div>
    )
  }
  return (
    <div className="my-2">
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="p-0 m-0">
              {aiSlot && toHourMinString(aiSlot?.exactTime)}
            </h4>
            <div className={'d-flex gap-2'}>
              <AppButton
                isLoading={isSlotLoading}
                buttonChild={<Floppy2 />}
                buttonVariant={'primary'}
                onClick={onSaveChanges}
                disabled={isSaveDisabled}
                tooltip="Uložit"
              ></AppButton>
              <AppButton
                isLoading={isSlotLoading || playlistLoading}
                buttonVariant={'primary'}
                onClick={onRegenerateSlot}
                buttonChild={<GearFill />}
                tooltip="Přegenerovat"
              ></AppButton>
              <AppButton
                onClick={() =>
                  navigate(`/playlist/${playlistId}/slot/${aiSlot?.id}`)
                }
                buttonVariant={'secondary'}
                buttonChild={<MusicNoteList />}
                tooltip="Přejít na moderátora"
              ></AppButton>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-column gap-2">
            <span>
              Plid <strong>{aiSlot?.plid}</strong>
            </span>
            <div className={'d-flex gap-4'}>
              <AppSelectComponent
                onSelectChange={onTypeSelectChange}
                options={aiTypeOption}
                valueId={selectedType}
                placeholder={'Vyberte typ'}
                className={'p-0 w-25'}
              ></AppSelectComponent>
            </div>
            <div>
              <div>
                Regionalizace&nbsp;
                <input
                  type="checkbox"
                  checked={regionalized}
                  onChange={handleCheckboxChange}
                />
              </div>
            </div>
            {aiMetadata?.articleSources &&
              aiMetadata?.articleSources.length > 0 && (
                <div>
                  <div>
                    <strong>Použité články</strong>
                    {aiMetadata?.articleSources.map((article, index) =>
                      articleSourceDetail(article, index)
                    )}
                  </div>
                </div>
              )}
            {aiMetadata?.textSource && (
              <div>
                <div>
                  <strong>Použité texty</strong>:<br />
                  <p className={'ms-2'}>{aiMetadata?.textSource}</p>
                </div>
              </div>
            )}
            <div>
              {aiMetadata?.textNarratives && (
                <div>
                  <strong>Použité narativy</strong>
                  {JSON.stringify(aiMetadata?.textNarratives)}
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

export default SlotOutlineComponent
